/* eslint-disable no-restricted-globals */
/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable new-cap */
/* eslint-disable no-plusplus */
import {
  IApiPdfExportOptions,
  IModal,
  IPrintPreviewUtil,
  isDingDing,
  isWeChat,
} from '@ibiz-template/runtime';
import { h } from 'vue';
import jsPDF from 'jspdf';
import { PrintPreviewMarkdown } from './print-preview-markdown/print-preview-markdown';
import { PrintHtml } from './print-html/print-html';
import {
  cleanupTempElements,
  createTempContainer,
  DEFAULT_OPTIONS,
  exportPdf,
  getCanvas,
  getCanvasImages,
  getPageSizeInPixels,
  validateOptions,
} from './print-pdf/pdf-helper';
/**
 * 打印预览工具类
 */
export class PrintPreviewUtil implements IPrintPreviewUtil {
  /**
   * 执行打印
   * @param context
   * @param params
   * @param data
   * @returns boolean 是否成功执行打印
   */
  async execPrint(
    context: IContext,
    params: IParams,
    data: Blob,
  ): Promise<boolean> {
    const srfcontenttype = params.srfcontenttype;
    if (srfcontenttype === 'MARKDOWN') {
      return this.printMarkDown(data, params);
    }
    if (srfcontenttype === 'HTML') {
      return this.printHtml(data, params);
    }
    return false;
  }

  /**
   * 打印MarkDown（窗口可以由视图参数srfprintheight和srfprintwidth指定，默认居中50%，绘制时为md预览模式绘制，提供全屏按钮，和关闭按钮。）
   * @param data
   * @returns
   */
  async printMarkDown(data: Blob, params: IParams): Promise<boolean> {
    const text = await data.text();
    const overlay = ibiz.overlay.createModal(
      (modal: IModal) =>
        h(PrintPreviewMarkdown, {
          value: text,
          modal,
        }),
      undefined,
      {
        width: params.srfprintwidth || '80%',
        height: params.srfprintheight || '80%',
      },
    );
    overlay.present();
    await overlay.onWillDismiss();
    return true;
  }

  /**
   * 打印HTML(原生浏览器预览)
   * @param data
   * @returns
   */
  async printHtml(data: Blob, params: IParams): Promise<boolean> {
    if (!isDingDing() || isWeChat()) {
      const content = await data.text();
      const overlay = ibiz.overlay.createModal(
        (modal: IModal) =>
          h(PrintHtml, {
            filename: params.srfprinttitle || 'HTML',
            content,
            modal,
          }),
        undefined,
        {
          width: params.srfprintwidth || '80%',
          height: params.srfprintheight || '80%',
          showClose: true,
        } as IData,
      );
      overlay.present();
      await overlay.onWillDismiss();
      return true;
    }
    const link = window.URL.createObjectURL(data);
    location.href = link;
    return true;
  }

  /**
   * @description 导出HTML文件
   * @param {string} filename 导出html文件名，需以.html结尾
   * @param {string} content 导出html内容，为body标签中的html字符串
   * @param {IData} [_style={}] 导出html样式，会添加到style标签中
   * @returns {*}  {Promise<boolean>}
   * @memberof PrintPreviewUtil
   */
  async printHtml2(
    filename: string,
    content: string,
    style: IData = {},
  ): Promise<boolean> {
    const overlay = ibiz.overlay.createModal(
      (modal: IModal) =>
        h(PrintHtml, {
          filename,
          content,
          modal,
          style,
        }),
      undefined,
      {
        width: style.width || '80%',
        height: style.height || '80%',
        showClose: true,
      } as IData,
    );
    overlay.present();
    await overlay.onWillDismiss();
    return true;
  }

  /**
   * @description 导出pdf
   * @param {string} filename 导出文件名，必须以.pdf结尾
   * @param {string} content 导出内容
   * @param {IApiPdfExportOptions} [_options={}]
   * @returns {*}  {Promise<boolean>}
   * @memberof PrintPreviewUtil
   */
  async printPdf(
    filename: string,
    content: string,
    options: IApiPdfExportOptions = {},
  ): Promise<boolean> {
    const data = {
      content,
      filename,
      ...options,
    };
    const validation = validateOptions(data);
    if (!validation) {
      return false;
    }
    let container: HTMLDivElement | null = null;

    try {
      const mergedOptions: IData = {
        ...DEFAULT_OPTIONS,
        ...data,
        margins: {
          ...DEFAULT_OPTIONS.margins,
          ...(data.margins || {}),
        },
      };

      container = createTempContainer(data.content, mergedOptions);

      const pageSizePixels = getPageSizeInPixels(
        mergedOptions.pageSize,
        mergedOptions.orientation,
      );

      const orientation = mergedOptions.orientation === 'portrait' ? 'p' : 'l';
      const pdf = new jsPDF({
        orientation,
        unit: 'mm',
        format: mergedOptions.pageSize,
      });

      const width =
        pageSizePixels.width -
        (mergedOptions.margins.left + mergedOptions.margins.right) *
          3.7795275591;

      // pdf a4分页宽高
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const innerWidth =
        pdfWidth - mergedOptions.margins.left - mergedOptions.margins.right;

      const innerHeight =
        pdfHeight - mergedOptions.margins.top - mergedOptions.margins.bottom;

      const containerHeight = container.scrollHeight;
      if (containerHeight > mergedOptions.segmentHeight) {
        const windowWidth = pageSizePixels.width;
        const segmentHeight = mergedOptions.segmentHeight!;
        Object.assign(mergedOptions, {
          width,
          windowWidth,
          segmentHeight,
          innerWidth,
          innerHeight,
        });
        const images = await getCanvas(container, mergedOptions);
        exportPdf(pdf, images, {
          innerWidth,
          innerHeight,
          mergedOptions,
          filename,
          showModel: true,
        });
      } else {
        const canvas = await ibiz.util.html2canvas.getCanvas(container, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: mergedOptions.backgroundColor,
          width,
          windowWidth: pageSizePixels.width,
          scrollY: 0,
          scrollX: 0,
        });
        // 每一页图片高度
        const pageHeight = (innerHeight / innerWidth) * width * 2;
        const images = getCanvasImages([canvas], {
          pageHeight,
          pageWidth: width * 2,
          innerHeight,
          innerWidth,
          backgroundColor: mergedOptions.backgroundColor,
        });
        exportPdf(pdf, images, {
          innerWidth,
          innerHeight,
          mergedOptions,
          filename,
          showModel: false,
        });
      }

      return true;
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : ibiz.i18n.t('util.printPreviewUtil.exportPdfFailed');
      ibiz.message.error(errorMessage);
      return false;
    } finally {
      cleanupTempElements(container);
    }
  }
}
